/**
 * Create.jsx
 * Compose a post: pick a brand, add an image, write a caption per platform,
 * review, publish.
 *
 * Phase 2 is deliberately manual — AI generation arrives in Phase 4 and will
 * fill these same fields rather than replace this screen. Getting publishing
 * proven first means the AI work can't hide a publishing bug.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import PublishConfirm from '../components/PublishConfirm.jsx';
import AiPanel from '../components/AiPanel.jsx';
import ImageLightbox from '../components/ImageLightbox.jsx';
import MediaPicker from '../components/MediaPicker.jsx';
import NoBrands from '../components/NoBrands.jsx';
import { Image as ImageIcon, Warning, Check, Plus, Clock } from '../components/Icons.jsx';
import { Link as RouterLink } from 'react-router-dom';
import BestTimes from '../components/BestTimes.jsx';
import ConnectionNotice, { readConnections } from '../components/ConnectionNotice.jsx';
import HashtagSets from '../components/HashtagSets.jsx';
import { defaultScheduleValue } from '../utils/time.js';

const LIMITS = { facebook: 5000, instagram: 2200 };

const FORMATS = {
  post: { label: 'Single post', platforms: ['facebook', 'instagram'], minMedia: 0, maxMedia: 1 },
  carousel: { label: 'Carousel', platforms: ['facebook', 'instagram'], minMedia: 2, maxMedia: 10 },
  story: {
    label: 'Story',
    platforms: ['facebook', 'instagram'],
    minMedia: 1,
    maxMedia: 1,
    noCaption: true,
  },
  link: {
    label: 'Link post',
    platforms: ['facebook'],
    minMedia: 0,
    maxMedia: 0,
    requiresLink: true,
  },
};

const FORMAT_HINTS = {
  post: 'One image or video, with a caption per platform.',
  carousel: 'Two to ten items people swipe through. The order below is the order they see.',
  story: 'Vertical, gone in 24 hours, no caption. Facebook takes images only.',
  link: 'A Facebook post with a preview card built from the URL.',
};

export default function Create() {
  const { brands, loading, error: brandsError } = useBrands();
  const fileInput = useRef(null);

  const [brandId, setBrandId] = useSelectedBrand();
  const [platforms, setPlatforms] = useState({ facebook: true, instagram: true });
  const [captions, setCaptions] = useState({ facebook: '', instagram: '' });
  const [firstComment, setFirstComment] = useState('');
  // An ordered list: one item for most formats, up to ten for a carousel.
  const [media, setMedia] = useState([]);
  const [format, setFormat] = useState('post');
  // Instagram picks its own Reel thumbnail unless we give it one.
  const [cover, setCover] = useState([]);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageWarnings, setImageWarnings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [saved, setSaved] = useState(null);
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState('now'); // 'now' | 'later'
  const [when, setWhen] = useState(defaultScheduleValue);

  // The default posting time from Settings, applied to tomorrow, so choosing
  // Schedule lands on a sensible slot rather than ten minutes from now.
  useEffect(() => {
    let cancelled = false;
    api.settings
      .get()
      .then(({ settings }) => {
        if (cancelled || !settings.default_post_time) return;
        const tomorrow = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
        setWhen(`${tomorrow}T${settings.default_post_time}`);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const brand = brands.find((b) => b.id === brandId) ?? null;
  const editingId = params.get('post');

  // Opening ?post=<id> loads that draft into the composer.
  useEffect(() => {
    if (!editingId || draftId === editingId) return;

    let cancelled = false;
    (async () => {
      try {
        const { post, targets } = await api.posts.get(editingId);
        if (cancelled) return;

        setDraftId(post.id);
        setBrandId(post.brand_id);
        setPlatforms({
          facebook: targets.some((t) => t.platform === 'facebook'),
          instagram: targets.some((t) => t.platform === 'instagram'),
        });
        setCaptions({
          facebook: targets.find((t) => t.platform === 'facebook')?.caption ?? '',
          instagram: targets.find((t) => t.platform === 'instagram')?.caption ?? '',
        });
        setFirstComment(targets.find((t) => t.platform === 'instagram')?.first_comment ?? '');

        const first = targets[0];
        setFormat(first?.format ?? 'post');
        setLinkUrl(first?.link_url ?? '');

        const ids = first?.media_ids ? JSON.parse(first.media_ids) : first?.media_id ? [first.media_id] : [];
        if (ids.length || first?.cover_media_id) {
          const { media: list } = await api.media.list(post.brand_id);
          setMedia(ids.map((id) => list.find((m) => m.id === id)).filter(Boolean));
          const saved = list.find((m) => m.id === first?.cover_media_id);
          setCover(saved ? [saved] : []);
        }
        if (post.scheduled_at_utc) setMode('later');
      } catch (e) {
        if (!cancelled) setError(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editingId, draftId]);

  // Opening ?media=<id> starts a new post with that library item attached —
  // how a video made on the Video screen goes out.
  const startingMediaId = params.get('media');
  useEffect(() => {
    if (!startingMediaId || editingId || !brandId) return undefined;

    let cancelled = false;
    api.media
      .list(brandId)
      .then(({ media: list }) => {
        if (cancelled) return;
        const found = list.find((m) => m.id === startingMediaId);
        if (found) setMedia((current) => (current.some((m) => m.id === found.id) ? current : [found]));
        // Off the address, so a reload does not attach it again over later changes.
        setParams(
          (p) => {
            p.delete('media');
            return p;
          },
          { replace: true },
        );
      })
      .catch((e) => !cancelled && setError(e));

    return () => {
      cancelled = true;
    };
  }, [startingMediaId, editingId, brandId]);

  // One reading of the account rows, shared with ConnectionNotice — two
  // readings of the same rows is how they drift apart and start disagreeing
  // about whether you can post.
  const connections = useMemo(() => readConnections(brand), [brand]);

  const available = useMemo(
    () => ({
      facebook: connections.facebook.available,
      instagram: connections.instagram.available,
    }),
    [connections],
  );

  useEffect(() => {
    // Only default the platforms for a new post; a loaded draft keeps its own.
    if (editingId) return;
    setPlatforms({ facebook: available.facebook, instagram: available.instagram });
  }, [available.facebook, available.instagram, editingId]);

  const buildTargets = useCallback(
    () =>
      Object.entries(platforms)
        .filter(([, on]) => on)
        .map(([platform]) => ({
          platform,
          format,
          caption: format === 'story' ? '' : captions[platform],
          media_ids: media.map((m) => m.id),
          link_url: format === 'link' ? linkUrl : null,
          cover_media_id: platform === 'instagram' ? (cover[0]?.id ?? null) : null,
          first_comment: platform === 'instagram' && format !== 'story' ? firstComment : '',
        })),
    [platforms, captions, media, firstComment, format, linkUrl, cover],
  );

  /** Creates the post if it is new, updates it if it already exists. */
  async function persist() {
    const targets = buildTargets();

    if (draftId) {
      const { post } = await api.posts.update(draftId, { targets });
      return post;
    }
    const { post } = await api.posts.create({ brand_id: brandId, targets });
    setDraftId(post.id);
    if (!editingId) setParams({ post: post.id }, { replace: true });
    return post;
  }

  async function saveDraft() {
    setBusy(true);
    setError(null);
    try {
      await persist();
      setSaved('Draft saved. It is on the Drafts screen whenever you want it.');
      setTimeout(() => setSaved(null), 4000);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function review() {
    setBusy(true);
    setError(null);
    try {
      await persist();
      setConfirming(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  function applyGenerated(option) {
    setCaptions((s) => ({
      facebook: option.captions.facebook ?? s.facebook,
      instagram: option.captions.instagram ?? s.instagram,
    }));
    if (option.hashtags) setFirstComment(option.hashtags);
  }

  function reset() {
    setCaptions({ facebook: '', instagram: '' });
    setFirstComment('');
    setMedia([]);
    setCover([]);
    setLinkUrl('');
    setFormat('post');
    setImageWarnings([]);
    setDraftId(null);
    setParams({}, { replace: true });
  }

  const chosen = Object.entries(platforms).filter(([, on]) => on).map(([p]) => p);
  const missingCaption = chosen.some((p) => !captions[p].trim());
  const tooLong = chosen.some((p) => captions[p].length > LIMITS[p]);
  const spec = FORMATS[format];
  const needsMedia = media.length < spec.minMedia || (platforms.instagram && media.length === 0);
  const needsLink = spec.requiresLink && !/^https?:\/\/\S+$/i.test(linkUrl.trim());
  const captionRequired = !spec.noCaption;
  const needsTime = mode === 'later' && !when;
  // Only a Reel has a cover: a video single post going to Instagram.
  const wantsCover = format === 'post' && platforms.instagram && media[0]?.kind === 'video';

  // Stories carry no caption, so a missing one is not a problem for them.
  const canReview =
    brandId &&
    chosen.length > 0 &&
    (!captionRequired || (!missingCaption && !tooLong)) &&
    !needsMedia &&
    !needsLink &&
    !needsTime;

  if (loading) return <p className="mono">Loading…</p>;

  if (brands.length === 0) {
    return <NoBrands error={brandsError} what="A post needs a brand to publish as." />;
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Create</div>
          <h1>{editingId ? 'Edit post' : 'New post'}</h1>
          <p>Write it yourself or generate it, review the destinations, then publish or schedule.</p>
        </div>
        <div className="page-head__actions">
          <button
            className="btn"
            onClick={saveDraft}
            disabled={busy || !brandId || chosen.length === 0}
            title="Keep this and come back to it"
          >
            Save draft
          </button>
          <button className="btn btn--primary" onClick={review} disabled={!canReview || busy}>
            {busy ? 'Working…' : mode === 'later' ? 'Review & schedule' : 'Review & publish'}
          </button>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {saved && (
        <div className="notice notice--info">
          <Check />
          <span>{saved}</span>
        </div>
      )}

      <div className="grid">
        {/* ---------- Left: brand, platforms, image ---------- */}
        <section className="card">
          <div className="card__head">
            <div className="card__head-text">
              <h2>Setup</h2>
              <p>Who this is from, and where it goes.</p>
            </div>
          </div>

          <div className="field">
            <label htmlFor="brand">Brand</label>
            <select id="brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Publish to</label>
            <div className="btn-row">
              {['facebook', 'instagram'].map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`btn btn--sm ${platforms[p] ? 'btn--primary' : ''}`}
                  disabled={!available[p]}
                  title={
                    available[p]
                      ? ''
                      : connections[p].exists
                        ? `${brand?.name}'s ${p} connection has stopped working — reconnect on Brands`
                        : `${brand?.name} has no ${p} account connected yet`
                  }
                  onClick={() => setPlatforms((s) => ({ ...s, [p]: !s[p] }))}
                >
                  {platforms[p] ? <Check size={15} /> : null}
                  {p === 'facebook' ? 'Facebook' : 'Instagram'}
                </button>
              ))}
            </div>
            {/* A connection that has broken and one that was never made need
                different sentences, because they send you to different places.
                Saying "no accounts yet" about an account that published
                yesterday sends you looking for a setup step you already did. */}
            <ConnectionNotice brand={brand} compact />

            {!available.instagram &&
              available.facebook &&
              connections.neverConnected.includes('instagram') && (
                <p className="hint">
                  {brand?.name} has no Instagram account connected, so this post can only go to
                  Facebook.
                </p>
              )}

            {!connections.anyAvailable && connections.broken.length === 0 && (
              <p className="hint">
                {brand?.name} has no connected accounts yet —{' '}
                <RouterLink to="/brands">connect them on the Brands screen</RouterLink> before
                posting.
              </p>
            )}
          </div>

          <div className="field">
            <label>When</label>
            <div className="btn-row">
              <button
                type="button"
                className={`btn btn--sm ${mode === 'now' ? 'btn--primary' : ''}`}
                onClick={() => setMode('now')}
              >
                {mode === 'now' ? <Check size={15} /> : null}
                Publish now
              </button>
              <button
                type="button"
                className={`btn btn--sm ${mode === 'later' ? 'btn--primary' : ''}`}
                onClick={() => setMode('later')}
              >
                {mode === 'later' ? <Clock size={15} /> : null}
                Schedule
              </button>
            </div>

            {mode === 'later' && (
              <>
                <input
                  type="datetime-local"
                  value={when}
                  min={defaultScheduleValue()}
                  onChange={(e) => setWhen(e.target.value)}
                  style={{ marginTop: 10 }}
                />
                {/* A recommendation you have to type in yourself is one you
                    will ignore, so each of these sets the field. */}
                <BestTimes brandId={brandId} variant="strip" onPick={setWhen} />

                <p className="hint">
                  London time. The scheduler checks every minute, so it goes out within a minute of
                  the slot — even with your PC switched off.
                </p>
              </>
            )}
          </div>

          <div className="field">
            <label>Post type</label>
            <div className="btn-row">
              {Object.entries(FORMATS).map(([key, f]) => {
                const unavailable = chosen.some((p) => !f.platforms.includes(p));
                return (
                  <button
                    key={key}
                    type="button"
                    className={`btn btn--sm ${format === key ? 'btn--primary' : ''}`}
                    disabled={unavailable}
                    title={
                      unavailable
                        ? `Not available on ${chosen
                            .filter((p) => !f.platforms.includes(p))
                            .join(' and ')}`
                        : ''
                    }
                    onClick={() => {
                      setFormat(key);
                      if (media.length > f.maxMedia) setMedia(media.slice(0, f.maxMedia));
                    }}
                  >
                    {format === key ? <Check size={15} /> : null}
                    {f.label}
                  </button>
                );
              })}
            </div>
            <p className="hint">{FORMAT_HINTS[format]}</p>
          </div>

          {format === 'link' && (
            <div className="field">
              <label htmlFor="link">Link</label>
              <input
                id="link"
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://archevot.com"
              />
              <p className="hint">
                Facebook builds the preview card from this page. Instagram cannot post links, so
                this format is Facebook only.
              </p>
            </div>
          )}

          {spec.maxMedia > 0 && (
            <MediaPicker
              brandId={brandId}
              selected={media}
              max={spec.maxMedia}
              onChange={setMedia}
              onWarnings={setImageWarnings}
              onView={setViewing}
            />
          )}

          {wantsCover && (
            <div className="cover">
              <MediaPicker
                brandId={brandId}
                selected={cover}
                max={1}
                kind="image"
                label="Cover frame (optional)"
                onChange={setCover}
                onView={setViewing}
              />
              <p className="hint">
                The still people see before the Reel plays. Leave this empty and Instagram picks a
                frame itself.
              </p>
            </div>
          )}

          {imageWarnings.map((w) => (
            <div className="notice notice--error" key={w}>
              <Warning />
              <span>{w}</span>
            </div>
          ))}
        </section>

        {/* ---------- Right: captions ---------- */}
        <section className="card">
          <div className="card__head">
            <div className="card__head-text">
              <h2>Captions</h2>
              <p>Each platform gets its own wording — that is the point.</p>
            </div>
          </div>

          {spec.noCaption && (
            <div className="empty">
              <div className="empty__art">
                <Check size={30} />
              </div>
              <h3>Stories have no caption</h3>
              <p>Any words need to be part of the image or video itself.</p>
            </div>
          )}

          {!spec.noCaption && platforms.facebook && (
            <div className="field">
              <label htmlFor="fb">
                Facebook
                <span className={`counter ${captions.facebook.length > LIMITS.facebook ? 'over' : ''}`}>
                  {captions.facebook.length} / {LIMITS.facebook}
                </span>
              </label>
              <textarea
                id="fb"
                value={captions.facebook}
                onChange={(e) => setCaptions((s) => ({ ...s, facebook: e.target.value }))}
                placeholder="Longer, explanatory, with a clear call to action."
                style={{ minHeight: 150 }}
              />
            </div>
          )}

          {!spec.noCaption && platforms.instagram && (
            <>
              <div className="field">
                <label htmlFor="ig">
                  Instagram
                  <span
                    className={`counter ${captions.instagram.length > LIMITS.instagram ? 'over' : ''}`}
                  >
                    {captions.instagram.length} / {LIMITS.instagram}
                  </span>
                </label>
                <textarea
                  id="ig"
                  value={captions.instagram}
                  onChange={(e) => setCaptions((s) => ({ ...s, instagram: e.target.value }))}
                  placeholder="Shorter and punchier. Hashtags go below."
                  style={{ minHeight: 120 }}
                />
              </div>

              <div className="field">
                <label htmlFor="fc">First comment</label>
                <input
                  id="fc"
                  type="text"
                  value={firstComment}
                  onChange={(e) => setFirstComment(e.target.value)}
                  placeholder={brand?.default_hashtags || '#hashtags go here'}
                />
                {/* Research that lives only on its own screen is research
                    nobody uses; the set saved on Tuesday belongs one click from
                    the post being written on Friday. */}
                <HashtagSets brandId={brandId} value={firstComment} onChange={setFirstComment} />

                <p className="hint">
                  Posted as a comment straight after publishing, so hashtags stay out of the caption.
                </p>
              </div>
            </>
          )}

          {chosen.length === 0 && (
            <div className="empty">
              <div className="empty__art">
                <ImageIcon size={30} />
              </div>
              <h3>No platform selected</h3>
              <p>
                Choose Facebook, Instagram or both on the left, and a caption box appears for each.
              </p>
            </div>
          )}

          {needsMedia && (
            <div className="notice notice--error">
              <Warning />
              <span>
                {spec.minMedia > 1
                  ? `A carousel needs at least ${spec.minMedia} items.`
                  : 'Instagram posts must have an image or a video.'}
              </span>
            </div>
          )}
        </section>
      </div>

      {brand && (
        <AiPanel brand={brand} platforms={platforms} onCaptions={applyGenerated} />
      )}

      {viewing && (
        <ImageLightbox
          src={api.media.rawUrl(viewing.id)}
          isVideo={viewing.kind === 'video'}
          alt={viewing.alt_text || ''}
          caption={`${viewing.kind === 'video' ? 'Video · ' : ''}${viewing.width}×${viewing.height}${
            viewing.aspect ? ` · ${viewing.aspect}` : ''
          }${viewing.duration_seconds ? ` · ${formatDuration(viewing.duration_seconds)}` : ''}`}
          onClose={() => setViewing(null)}
        />
      )}

      {confirming && draftId && (
        <PublishConfirm
          postId={draftId}
          brandName={brand?.name}
          imageId={media[0]?.id}
          isVideo={media[0]?.kind === 'video'}
          mode={mode === 'later' ? 'schedule' : 'publish'}
          whenLocal={when}
          onPublished={reset}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
